import { useState, useEffect } from "react";
import { Plan } from "../../types/Plan";
import firebase from "../firebase";
import DB_PATH from "../DB_PATH";

export type PlansResponse = {
  loading: boolean;
  error: boolean;
  message: string;
  data: {
    plans: Array<Plan>;
    userPlans: Array<UserPlan>;
    combined: Array<Plan | UserPlan>;
  };
};

export type UserPlan = {
  id: string;
  [key: string]: any;
};

export default ({
  communityId,
  refreshKey = 0,
}: {
  communityId: string;
  refreshKey?: number;
}): PlansResponse => {
  const [plans, setPlans] = useState<PlansResponse>({
    loading: true,
    error: false,
    data: { plans: [], userPlans: [], combined: [] },
    message: "",
  });

  useEffect(() => {
    if (!communityId) return;

    const firestore = firebase.firestore();

    const plansCollection = firestore
      .collection(DB_PATH.PLAN)
      .where("communityId", "==", communityId);

    const userPlansCollection = firestore
      .collection(DB_PATH.USER_PLANS)
      .where("communityId", "==", communityId);

    let plansData: Plan[] = [];
    let userPlansData: UserPlan[] = [];

    const updateState = () =>
      setPlans({
        loading: false,
        error: false,
        message: "",
        data: {
          plans: plansData,
          userPlans: userPlansData,
          combined: [...plansData, ...userPlansData],
        },
      });

    const unsubscribePlans = plansCollection.onSnapshot(
      (snapshot) => {
        plansData = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Plan)
        );

        updateState();
      },
      (error) => {
        setPlans({
          loading: false,
          error: true,
          message: error.message,
          data: { plans: [], userPlans: [], combined: [] },
        });
      }
    );

    const unsubscribeUserPlans = userPlansCollection.onSnapshot(
      (snapshot) => {
        userPlansData = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as UserPlan)
        );

        updateState();
      },
      (error) => {
        setPlans({
          loading: false,
          error: true,
          message: error.message,
          data: { plans: [], userPlans: [], combined: [] },
        });
      }
    );

    // Cleanup function to unsubscribe from the listener when the component unmounts
    return () => {
      unsubscribePlans();
      unsubscribeUserPlans();
    };
  }, [communityId, refreshKey]);

  return plans;
};
